import { Link } from 'react-router-dom';
import { cn } from '@attendance/ui/utils';
import type { LucideIcon } from 'lucide-react';
import { COLLAPSED_LABEL, NAV_ICON, RAIL_FOCUS } from './railSkin.js';

/* ===========================================================================
 * THE SURFACE SWITCH — the one row on each side of the rail that points at the
 * other side.
 *
 * Out of `Sidebar.tsx` because it is a whole control with one reason to change
 * (how the personal workspace and the Admin panel link to each other), and
 * `Sidebar.tsx` is long-standing size debt behind a ratchet. Nothing about it
 * changed in the move except the hover ground, which now comes from the rail's
 * brand-mixed token like every row above it.
 * ======================================================================== */

/**
 * The switch between the personal workspace and the Admin panel. An outlined
 * pill rather than a rail row, because it changes SURFACE rather than page.
 */
export function SurfaceSwitch({
  to,
  icon: Icon,
  mirrorRtl,
  label,
  collapsed,
}: {
  to: string;
  icon: LucideIcon;
  /** Set for a DIRECTIONAL glyph (an arrow); it flips under `dir="rtl"`. */
  mirrorRtl?: boolean;
  label: string;
  collapsed: boolean;
}): JSX.Element {
  return (
    <div>
      {/* `Link`, not `NavLink`: this pill can never be the current page — it is
          rendered only from the surface it is pointing AWAY from — so the
          active-state machinery would only ever add an unused `active` class. */}
      <Link
        to={to}
        title={collapsed ? label : undefined}
        className={cn(
          // PADDING PLUS A FLOOR, which is the anatomy §0 prescribes and what
          // this pill had lost. Its only height was the 44px touch minimum, so
          // when that was gated behind `[@media(pointer:coarse)]` the desktop
          // pill collapsed to the height of its own text — measured at `0px
          // 12px` against the handoff's `padding: 10px 12px`
          // (`Hadir Check-in Desktop.dc.html:98`, and the mirroring "Back to my
          // workspace" at `Hadir Dashboard.dc.html:43`). `py-2.5` is that 10px,
          // and the coarse floor still applies on top of it.
          'flex [@media(pointer:coarse)]:min-h-touch items-center gap-2.5 rounded-control border py-2.5 ps-3 pe-3 text-copy font-semibold',
          // `/15`, not `/20` — the handoff draws this edge at
          // `rgba(255,255,255,.16)` and 15 is the scale step that lands on it.
          // (The rail's DIVIDER stays at /20: that value was measured against
          // ink for a hairline that has to survive being looked past, which is
          // a different job from a control's own edge.)
          'border-nav-border text-nav-on-surface-muted transition-colors duration-fast',
          /* The same brand-mixed hover ground the rows take. It was
             `bg-nav-on-surface/10` — the neutral wash — so the one control that
             changes SURFACE lit up in a different colour from every row above
             it once the rows went brand. One rail, one hover. */
          'hover:border-nav-border-strong hover:bg-nav-hover-surface hover:text-nav-on-surface',
          RAIL_FOCUS,
          collapsed && 'md:justify-center md:ps-0 md:pe-0'
        )}
      >
        <Icon
          {...NAV_ICON}
          className={cn('shrink-0', mirrorRtl && 'rtl:-scale-x-100')}
          aria-hidden
        />
        <span className={cn('truncate', collapsed && COLLAPSED_LABEL)}>{label}</span>
      </Link>
    </div>
  );
}
